import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, Platform } from 'react-native';
import {
  User as FrontendUser,
  UserRole,
  Permission,
  SubscriptionPlan,
} from '../types/auth';
import {
  saveToken,
  getToken,
  clearAllAppStorage,
  getPendingOnboarding,
  clearPendingOnboarding,
} from '../utils/storage';
import {
  authAPI,
  BackendUser,
  LoginRequest,
  RegisterRequest,
} from '../api/auth';
import { onboardingAPI } from '../api/onboarding';
import PermissionService from '../services/PermissionService';
import { isCookieAuthMode } from '../utils/authMode';
import {
  getAuthErrorCachePatch,
  getSignUpSuccessCachePatch,
  isVerificationOnlySignUpResponse,
  shouldKeepPersistedSessionFallback,
  isAuthenticatedWithSession,
} from '../utils/authSignInSignUp';

// Storage keys
const USER_DATA_KEY = 'user_data';
const AUTH_TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const TOKEN_EXPIRES_KEY = 'token_expires';

// Query keys
export const authKeys = {
  all: ['auth'] as const,
  user: () => [...authKeys.all, 'user'] as const,
  session: () => [...authKeys.all, 'session'] as const,
  pendingEmailVerify: () => [...authKeys.all, 'pendingEmailVerify'] as const,
};

// Convert BackendUser to frontend User type
const mapUser = (backendUser: BackendUser): FrontendUser => ({
  id: backendUser.id,
  email: backendUser.email,
  name: backendUser.name,
  role: (backendUser.role as UserRole) || UserRole.USER,
  subscription_plan:
    (backendUser.subscription_plan as SubscriptionPlan) ||
    SubscriptionPlan.FREE,
  profileImageUrl: backendUser.profile_image_url,
  bio: backendUser.bio,
  onboarding_completed: backendUser.onboarding_completed,
  is_admin: backendUser.is_admin || false,
  color_generator_used_at: backendUser.color_generator_used_at,
  virtual_drape_used_at: backendUser.virtual_drape_used_at,
  color_analysis_used_at: backendUser.color_analysis_used_at,
  email_verified: backendUser.email_verified ?? true,
});

// Load persisted session
const loadPersistedSession = async (): Promise<{
  user: FrontendUser | null;
  token: string | null;
}> => {
  try {
    if (isCookieAuthMode()) {
      // Cookie mode: probe /me with credentials; no tokens in storage
      try {
        const user = await authAPI.getCurrentUser('');
        const frontendUser = mapUser(user);
        await saveToken(USER_DATA_KEY, JSON.stringify({ id: frontendUser.id }));
        return { user: frontendUser, token: 'cookie' };
      } catch {
        return { user: null, token: null };
      }
    }

    const [
      storedUserData,
      storedToken,
      storedRefreshToken,
      storedTokenExpires,
    ] = await Promise.all([
      getToken(USER_DATA_KEY),
      getToken(AUTH_TOKEN_KEY),
      getToken(REFRESH_TOKEN_KEY),
      getToken(TOKEN_EXPIRES_KEY),
    ]);

    if (storedUserData && storedToken) {
      try {
        const now = new Date().getTime();
        const tokenExpires = storedTokenExpires
          ? parseInt(storedTokenExpires)
          : 0;
        let accessToken = storedToken;

        if (tokenExpires > 0 && now > tokenExpires) {
          if (storedRefreshToken) {
            try {
              const refreshResponse =
                await authAPI.refreshToken(storedRefreshToken);
              if (!refreshResponse.success || !refreshResponse.data) {
                throw new Error(
                  refreshResponse.error ||
                    refreshResponse.detail ||
                    'Token refresh failed'
                );
              }
              accessToken = refreshResponse.data.access_token;
              const newExpires = now + refreshResponse.data.expires_in * 1000;
              await Promise.all([
                saveToken(AUTH_TOKEN_KEY, accessToken),
                saveToken(
                  REFRESH_TOKEN_KEY,
                  refreshResponse.data.refresh_token
                ),
                saveToken(TOKEN_EXPIRES_KEY, newExpires.toString()),
              ]);
            } catch {
              await clearStoredSession();
              return { user: null, token: null };
            }
          } else {
            await clearStoredSession();
            return { user: null, token: null };
          }
        }

        const user = await authAPI.getCurrentUser(accessToken);
        const frontendUser = mapUser(user);
        return { user: frontendUser, token: accessToken };
      } catch {
        // Only keep a local fallback while onboarding is incomplete (token still needed for Complete Setup).
        // Fully onboarded users with a failed /me get cleared to avoid stale "logged in" state on mobile web.
        try {
          const parsed = JSON.parse(storedUserData);
          const fallbackUser = parsed?.id
            ? { id: parsed.id, email: parsed.email, ...parsed }
            : null;
          if (
            fallbackUser &&
            shouldKeepPersistedSessionFallback(fallbackUser)
          ) {
            return { user: fallbackUser as FrontendUser, token: accessToken };
          }
        } catch {
          /* parse failed */
        }
        await clearStoredSession();
        return { user: null, token: null };
      }
    }
    return { user: null, token: null };
  } catch {
    await clearStoredSession();
    return { user: null, token: null };
  }
};

// Persist user session
// On web: store only { id } to minimize PII in localStorage (XSS risk)
// On native: store full user (SecureStore is encrypted)
// Cookie mode: only store user_data (id); tokens in httpOnly cookies
const persistUserSession = async (
  userData: FrontendUser,
  accessToken: string,
  refreshToken?: string,
  expiresIn?: number
) => {
  try {
    if (isCookieAuthMode()) {
      await saveToken(USER_DATA_KEY, JSON.stringify({ id: userData.id }));
      return;
    }

    const userDataToStore =
      Platform.OS === 'web' ? { id: userData.id } : userData;
    const promises = [
      saveToken(USER_DATA_KEY, JSON.stringify(userDataToStore)),
      saveToken(AUTH_TOKEN_KEY, accessToken),
    ];

    if (refreshToken) {
      promises.push(saveToken(REFRESH_TOKEN_KEY, refreshToken));
    }

    if (expiresIn) {
      const expiresAt = new Date().getTime() + expiresIn * 1000;
      promises.push(saveToken(TOKEN_EXPIRES_KEY, expiresAt.toString()));
    }

    await Promise.all(promises);
  } catch {
    // Persist failed - silent to avoid breaking flow; onboarding will handle missing token
  }
};

const clearStoredSession = async () => {
  try {
    if (isCookieAuthMode()) {
      await authAPI.logout();
    }
    let userId: string | null = null;
    try {
      const userData = await getToken(USER_DATA_KEY);
      if (userData) {
        const parsed = JSON.parse(userData);
        userId =
          parsed?.id != null ? String(parsed.id) : (parsed?.email ?? null);
      }
    } catch {
      /* Clear failed - continue to clearAllAppStorage */
    }
    await clearAllAppStorage(userId);
  } catch {
    // Clear failed
  }
};

// Hook to get current user
export const useUser = () => {
  return useQuery({
    queryKey: authKeys.user(),
    queryFn: async () => {
      const session = await loadPersistedSession();
      if (session.user && session.token) {
        // Refresh user data from API
        try {
          const user = await authAPI.getCurrentUser(session.token);
          return mapUser(user);
        } catch {
          await clearStoredSession();
          return null;
        }
      }
      return null;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });
};

// Hook to get auth token
export const useAuthToken = () => {
  return useQuery({
    queryKey: authKeys.session(),
    queryFn: async () => {
      const token = await getToken(AUTH_TOKEN_KEY);
      return token;
    },
    staleTime: Infinity,
  });
};

// Main auth hook
export const useAuth = () => {
  const queryClient = useQueryClient();
  const { data: user, isLoading: isLoadingUser } = useUser();
  const { data: token } = useAuthToken();
  const { data: pendingEmailVerify = false } = useQuery({
    queryKey: authKeys.pendingEmailVerify(),
    queryFn: () => false,
    initialData: false,
    staleTime: Infinity,
  });

  const isAuthenticated = isAuthenticatedWithSession({
    user,
    token,
    cookieAuthMode: isCookieAuthMode(),
  });
  const role = user?.role || UserRole.USER;
  const subscriptionPlan = user?.subscription_plan || SubscriptionPlan.FREE;

  const resetAuthCacheAfterPersistFailure = () => {
    const patch = getAuthErrorCachePatch();
    queryClient.setQueryData(authKeys.user(), patch.user);
    queryClient.setQueryData(authKeys.session(), patch.session);
  };

  // Sign in mutation
  const signInMutation = useMutation({
    mutationFn: async (credentials: LoginRequest) => {
      const authResponse = await authAPI.login(credentials);
      if (!authResponse.success || !authResponse.data) {
        throw new Error(
          authResponse.error ||
            authResponse.detail ||
            authResponse.message ||
            'Login failed'
        );
      }

      const tempUser = {
        id: '',
        email: credentials.email,
        name: '',
        role: UserRole.USER,
        subscription_plan: SubscriptionPlan.FREE,
        onboarding_completed: false,
      };

      try {
        await persistUserSession(
          tempUser,
          authResponse.data.access_token,
          authResponse.data.refresh_token,
          authResponse.data.expires_in
        );

        const user = await authAPI.getCurrentUser(
          authResponse.data.access_token
        );
        const frontendUser = mapUser(user);

        await persistUserSession(
          frontendUser,
          authResponse.data.access_token,
          authResponse.data.refresh_token,
          authResponse.data.expires_in
        );

        return { user: frontendUser, token: authResponse.data.access_token };
      } catch (error) {
        await clearStoredSession();
        resetAuthCacheAfterPersistFailure();
        throw error;
      }
    },
    onSuccess: async (data) => {
      queryClient.setQueryData(authKeys.user(), data.user);
      queryClient.setQueryData(authKeys.session(), data.token);
      if (data.user?.email_verified) {
        queryClient.setQueryData(authKeys.pendingEmailVerify(), false);
      }

      // If backend shows onboarding incomplete but we have pending data (save failed earlier), retry now
      let user = data.user;
      if (!user.onboarding_completed) {
        const pending = await getPendingOnboarding();
        if (pending && data.token) {
          try {
            const updated = await onboardingAPI.complete(pending, data.token);
            await clearPendingOnboarding();
            user = mapUser(updated);
            queryClient.setQueryData(authKeys.user(), user);
          } catch {
            /* Retry failed - will show Onboarding */
          }
        }
      }
    },
    onError: async (error: Error) => {
      if (error.message?.includes('timed out')) {
        throw new Error(
          'Server is starting up. Please wait a moment and try again.'
        );
      }
      throw error;
    },
  });

  // Sign up mutation
  const signUpMutation = useMutation({
    mutationFn: async (credentials: RegisterRequest) => {
      const authResponse = await authAPI.register(credentials);
      if (!authResponse.success) {
        throw new Error(
          authResponse.error ||
            authResponse.detail ||
            authResponse.message ||
            'Sign up failed'
        );
      }
      // Email verification required AND no tokens: user must verify before signing in.
      // When we have tokens, user can complete onboarding first; verify prompt shown after.
      if (isVerificationOnlySignUpResponse(authResponse)) {
        return {
          requiresVerification: true,
          email: credentials.email,
          message:
            authResponse.message ||
            'Please check your email and verify your account before signing in.',
        };
      }

      const accessToken = authResponse.data.access_token;
      const refreshToken = authResponse.data.refresh_token;
      const expiresIn = authResponse.data.expires_in;

      if (!accessToken) {
        throw new Error('Registration succeeded but no access token received.');
      }
      if (!refreshToken) {
        throw new Error(
          'Registration succeeded but no refresh token received. Please try signing in.'
        );
      }

      const tempUser = {
        id: '',
        email: credentials.email,
        name: credentials.name || '',
        role: UserRole.USER,
        subscription_plan: SubscriptionPlan.FREE,
        onboarding_completed: false,
      };

      try {
        await persistUserSession(
          tempUser,
          accessToken,
          refreshToken,
          expiresIn
        );

        const user = await authAPI.getCurrentUser(accessToken);
        // Trust the registration response's verification flag rather than the /me check,
        // because the /me Supabase admin lookup can fail on new accounts and returns true by default.
        const requiresEmailVerification =
          !!authResponse.requires_email_verification;
        const mapped = mapUser(user);
        const frontendUser = requiresEmailVerification
          ? { ...mapped, email_verified: false }
          : mapped;

        await persistUserSession(
          frontendUser,
          accessToken,
          refreshToken,
          expiresIn
        );

        return {
          user: frontendUser,
          token: authResponse.data.access_token,
          requiresEmailVerification,
        };
      } catch (error) {
        await clearStoredSession();
        resetAuthCacheAfterPersistFailure();
        throw error;
      }
    },
    onSuccess: (data) => {
      const patch = getSignUpSuccessCachePatch(data);
      queryClient.setQueryData(authKeys.user(), patch.user);
      queryClient.setQueryData(authKeys.session(), patch.session);
      if (patch.pendingEmailVerify) {
        queryClient.setQueryData(authKeys.pendingEmailVerify(), true);
      }
    },
    onError: (error: Error) => {
      Alert.alert(
        'Error',
        error.message || 'Sign up failed. Please try again.'
      );
      throw error;
    },
  });

  // Sign out mutation
  const signOutMutation = useMutation({
    mutationFn: async () => {
      await clearStoredSession();
    },
    onSuccess: () => {
      queryClient.setQueryData(authKeys.user(), null);
      queryClient.setQueryData(authKeys.session(), null);
      queryClient.invalidateQueries({ queryKey: authKeys.all });
    },
  });

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: async ({
      currentPassword,
      newPassword,
    }: {
      currentPassword: string;
      newPassword: string;
    }) => {
      if (!token) {
        throw new Error('Authentication token not found');
      }
      const response = await authAPI.changePassword(
        {
          current_password: currentPassword,
          new_password: newPassword,
        },
        token
      );
      if (!response.success) {
        throw new Error(
          response.error || response.detail || 'Failed to change password'
        );
      }
      return response;
    },
    onSuccess: () => {
      Alert.alert(
        'Success',
        'Password changed successfully! Please log in with your new password.'
      );
      clearStoredSession();
      queryClient.setQueryData(authKeys.user(), null);
      queryClient.setQueryData(authKeys.session(), null);
    },
    onError: (error: Error) => {
      Alert.alert(
        'Error',
        error.message || 'Failed to change password. Please try again.'
      );
      throw error;
    },
  });

  const resendVerificationMutation = useMutation({
    mutationFn: async (email: string) => {
      const response = await authAPI.resendVerification(email);
      if (!response.success) {
        throw new Error(
          response.error ||
            response.detail ||
            response.message ||
            'Failed to resend verification email.'
        );
      }
      return { email };
    },
    onSuccess: () => {
      Alert.alert(
        'Check your email',
        "If an account exists for that email, we've sent a new verification link."
      );
    },
    onError: (error: Error) => {
      Alert.alert(
        'Error',
        error.message || 'Failed to resend verification email.'
      );
      throw error;
    },
  });

  const requestPasswordResetMutation = useMutation({
    mutationFn: async (email: string) => {
      const response = await authAPI.requestPasswordReset(email);
      if (!response.success) {
        throw new Error(
          response.error ||
            response.detail ||
            response.message ||
            'Failed to send reset email.'
        );
      }
      return { email };
    },
    onSuccess: () => {
      Alert.alert(
        'Check your email',
        "If an account exists for that email, we've sent a link to reset your password."
      );
    },
    onError: (error: Error) => {
      Alert.alert(
        'Error',
        error.message || 'Failed to send reset email. Please try again.'
      );
      throw error;
    },
  });

  const forgotPasswordMutation = useMutation({
    mutationFn: async ({ email }: { email: string; newPassword?: string }) => {
      const response = await authAPI.forgotPassword({ email });
      if (!response.success) {
        const errorMsg =
          response.error ||
          response.detail ||
          response.message ||
          'Failed to send reset email';
        throw new Error(errorMsg);
      }
      return response;
    },
    onSuccess: (response) => {
      const message =
        response.message ||
        'If an account exists with this email, a reset link has been sent. Check your inbox.';
      Alert.alert('Success', message);
    },
    onError: (error: Error) => {
      Alert.alert(
        'Error',
        error.message || 'Failed to reset password. Please try again.'
      );
      throw error;
    },
  });

  // Refresh user mutation
  const refreshUserMutation = useMutation({
    mutationFn: async () => {
      if (!token) {
        throw new Error('No token available');
      }
      const updatedUser = await authAPI.getCurrentUser(token);
      const frontendUser = mapUser(updatedUser);
      await persistUserSession(frontendUser, token);
      return frontendUser;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(authKeys.user(), data);
    },
  });

  // Helper functions
  const continueAsGuest = () => {
    queryClient.setQueryData(authKeys.user(), null);
    queryClient.setQueryData(authKeys.session(), null);
  };

  const upgradeToPremium = async () => {
    if (user) {
      const premiumUser = { ...user, subscription_plan: SubscriptionPlan.PRO };
      queryClient.setQueryData(authKeys.user(), premiumUser);
      try {
        const storedToken = await getToken(AUTH_TOKEN_KEY);
        if (storedToken) {
          await persistUserSession(premiumUser, storedToken);
        }
      } catch {
        // Update storage failed
      }
      Alert.alert('Success', 'Upgraded to Pro Plan!');
    } else {
      Alert.alert('Error', 'You need to be logged in to upgrade to premium');
    }
  };

  const startFreemiumTrial = async () => {
    if (user) {
      const trialUser = {
        ...user,
        subscription_plan: SubscriptionPlan.FREEMIUM_TRIAL,
      };
      queryClient.setQueryData(authKeys.user(), trialUser);
      try {
        const storedToken = await getToken(AUTH_TOKEN_KEY);
        if (storedToken) {
          await persistUserSession(trialUser, storedToken);
        }
      } catch {
        // Update storage failed
      }
      Alert.alert('Success', 'Started 7-day Freemium Trial!');
    } else {
      Alert.alert('Error', 'You need to be logged in to start a trial');
    }
  };

  const downgradeToFree = async () => {
    if (user) {
      const freeUser = { ...user, subscription_plan: SubscriptionPlan.FREE };
      queryClient.setQueryData(authKeys.user(), freeUser);
      try {
        const storedToken = await getToken(AUTH_TOKEN_KEY);
        if (storedToken) {
          await persistUserSession(freeUser, storedToken);
        }
      } catch {
        // Update storage failed
      }
      Alert.alert('Success', 'Downgraded to Free Tier');
    }
  };

  const hasPermission = (permission: Permission): boolean => {
    if (!user) return false;
    const userRole = role || UserRole.USER;
    const userSubscription = subscriptionPlan || SubscriptionPlan.FREE;
    return PermissionService.hasFullPermission(
      userRole,
      userSubscription,
      permission
    );
  };

  const needsOnboarding = (): boolean => {
    return isAuthenticated && (!user || !user.onboarding_completed);
  };

  const needsEmailVerification = (): boolean => {
    return (
      pendingEmailVerify === true ||
      (isAuthenticated &&
        !!user?.onboarding_completed &&
        user.email_verified === false)
    );
  };

  const setPendingEmailVerification = (value: boolean) => {
    queryClient.setQueryData(authKeys.pendingEmailVerify(), value);
  };

  const updateOnboardingStatus = async (completed: boolean) => {
    if (user) {
      const updatedUser = { ...user, onboarding_completed: completed };
      queryClient.setQueryData(authKeys.user(), updatedUser);
      try {
        const storedToken = await getToken(AUTH_TOKEN_KEY);
        if (storedToken) {
          await persistUserSession(updatedUser, storedToken);
        }
      } catch {
        // Update storage failed
      }
    }
  };

  // Wrapper functions to match the old API
  const signIn = async (email: string, password: string) => {
    return signInMutation.mutateAsync({ email, password });
  };

  const signUp = async (
    email: string,
    password: string,
    name?: string,
    inviteCode?: string
  ) => {
    return signUpMutation.mutateAsync({
      email,
      password,
      name,
      invite_code: inviteCode,
    });
  };

  return {
    user,
    token: token ?? null,
    role,
    subscriptionPlan,
    isAuthenticated,
    isLoading: isLoadingUser,
    isSigningIn: signInMutation.isPending,
    isSigningOut: signOutMutation.isPending,
    signIn,
    signUp,
    signOut: signOutMutation.mutateAsync,
    changePassword: changePasswordMutation.mutateAsync,
    requestPasswordReset: requestPasswordResetMutation.mutateAsync,
    resendVerification: resendVerificationMutation.mutateAsync,
    forgotPassword: forgotPasswordMutation.mutateAsync,
    continueAsGuest,
    upgradeToPremium,
    startFreemiumTrial,
    downgradeToFree,
    refreshUser: refreshUserMutation.mutateAsync,
    hasPermission,
    needsOnboarding,
    needsEmailVerification,
    setPendingEmailVerification,
    updateOnboardingStatus,
  };
};
